import React from 'react';
import { DashboardControl } from 'devexpress-dashboard';
import { localStorageService } from '../../services/localstorage-service';
import { UserRoleType } from '../../utils/user-role-type';
import { apiconst } from '../../utils/app-const';
import { DashboardPanelExtension } from 'devexpress-dashboard/common';
import { authenticationService } from '../../services/AuthService'
import { webDashboardService } from '../../services/WebDashboardService'
import { applyDashboardLocalization } from '../../localization/dashboard-localization'
import {
    buildYearMonthOnlyState,
    isPeriodPickerDialogOpen,
    parseDashboardState,
    reconcilePeriodFilters
} from './period-filter-sync'
import { onYearMonthComboWidgetOptionsPrepared } from './year-month-combo-display'
import { onCommissionPercentGridWidgetOptionsPrepared } from './commission-percent-grid-display'
import { formatYearMonthLabel } from './year-month-labels'

const emptyConditionalFormattingModel = {
    RuleModels: [],
    FormatConditionStyleSettings: []
};

/** Default PDF export: A4 landscape (DevExpress default is Letter + Portrait). */
const basePdfExportOptions = {
    PaperKind: 'A4',
    PageLayout: 'Landscape',
    ExportFilters: true,
    ShowTitle: true
};

/** DevExpress default is Auto height — items shrink to fit the viewport and KPI values get clipped. */
const DASHBOARD_LAYOUT_MIN_HEIGHT = 960;
const DASHBOARD_TOOLBAR_HEIGHT = 56;
const SUPPLIER_PAYOUT_DASHBOARD_ID = '1202';

function isPayoutDashboard(dashboardId) {
    return String(dashboardId ?? '') === SUPPLIER_PAYOUT_DASHBOARD_ID;
}

function getPayoutDashboardHeight() {
    const layoutBody = document.querySelector('.side-nav-outer-toolbar .layout-body');
    if (layoutBody?.clientHeight > 100) {
        return layoutBody.clientHeight;
    }
    return window.innerHeight - DASHBOARD_TOOLBAR_HEIGHT;
}

function getDashboardLayoutHeight(dashboardId) {
    if (isPayoutDashboard(dashboardId)) {
        return getPayoutDashboardHeight();
    }
    const viewportHeight = window.innerHeight - DASHBOARD_TOOLBAR_HEIGHT;
    return Math.max(DASHBOARD_LAYOUT_MIN_HEIGHT, viewportHeight);
}

function applyDashboardLayoutHeight(dashboard, dashboardId) {
    if (!dashboard?.layoutOptions) {
        return;
    }
    const id = dashboardId
        ?? dashboard.control?.dashboardId?.()
        ?? dashboard.control?.option?.('dashboardId');
    dashboard.layoutOptions.width.mode('Auto');
    dashboard.layoutOptions.height.mode('Fixed');
    dashboard.layoutOptions.height.value(getDashboardLayoutHeight(id));
}

function getFirstMasterFilterValue(state, itemName) {
    const values = state?.Items?.[itemName]?.MasterFilterValues;
    if (!values?.length || !values[0]?.length) {
        return null;
    }
    return values[0][0];
}

function buildPdfExportOptions(dashboardControl) {
    const options = { ...basePdfExportOptions };
    if (!dashboardControl?.getDashboardState) {
        return options;
    }

    const dashboardId = String(dashboardControl.dashboardId?.() ?? dashboardControl.option?.('dashboardId') ?? '');
    if (dashboardId !== SUPPLIER_PAYOUT_DASHBOARD_ID) {
        return options;
    }

    try {
        const state = parseDashboardState(dashboardControl.getDashboardState());
        const supplierName = getFirstMasterFilterValue(state, 'comboBoxSupplier');
        const yearMonth = getFirstMasterFilterValue(state, 'comboBoxYearMonth');
        const parts = ['Salgsoversikt (utbetaling)'];
        if (supplierName) {
            parts.push(String(supplierName));
        }
        if (yearMonth) {
            parts.push(formatYearMonthLabel(yearMonth) || String(yearMonth));
        }
        if (parts.length > 1) {
            options.Title = parts.join(' — ');
        }
    } catch {
        // Keep default title when dashboard state is unavailable.
    }

    return options;
}

function patchDashboardItemResponse(data) {
    if (data && data.ConditionalFormattingModel === null) {
        data.ConditionalFormattingModel = emptyConditionalFormattingModel;
    }
    return data;
}

function isDashboardItemDataUrl(url) {
    return typeof url === 'string' && url.includes('DashboardItemGetAction');
}

function installDashboardAjaxCompatibility() {
    if (window.__dashboardAjaxCompatInstalled) {
        return;
    }
    window.__dashboardAjaxCompatInstalled = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
        if (!isDashboardItemDataUrl(url)) {
            return response;
        }
        try {
            const data = await response.clone().json();
            if (data?.ConditionalFormattingModel === null) {
                patchDashboardItemResponse(data);
                return new Response(JSON.stringify(data), {
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers
                });
            }
        } catch {
            // Keep original response when body is not JSON.
        }
        return response;
    };

    const xhrProto = XMLHttpRequest.prototype;
    const responseTextDescriptor = Object.getOwnPropertyDescriptor(xhrProto, 'responseText');
    const responseDescriptor = Object.getOwnPropertyDescriptor(xhrProto, 'response');
    const originalOpen = xhrProto.open;

    const patchXhrJsonResponse = (xhr, rawText) => {
        if (!rawText || xhr.readyState !== 4 || !isDashboardItemDataUrl(xhr._dashboardItemDataUrl)) {
            return rawText;
        }
        try {
            const data = JSON.parse(rawText);
            if (data?.ConditionalFormattingModel === null) {
                return JSON.stringify(patchDashboardItemResponse(data));
            }
        } catch {
            // Ignore non-JSON responses.
        }
        return rawText;
    };

    xhrProto.open = function (method, url, ...rest) {
        this._dashboardItemDataUrl = url;
        return originalOpen.call(this, method, url, ...rest);
    };

    if (responseTextDescriptor?.get) {
        const originalResponseTextGet = responseTextDescriptor.get;
        Object.defineProperty(xhrProto, 'responseText', {
            get: function () {
                return patchXhrJsonResponse(this, originalResponseTextGet.call(this));
            },
            configurable: true
        });
    }

    if (responseDescriptor?.get) {
        const originalResponseGet = responseDescriptor.get;
        Object.defineProperty(xhrProto, 'response', {
            get: function () {
                const raw = originalResponseGet.call(this);
                return typeof raw === 'string' ? patchXhrJsonResponse(this, raw) : raw;
            },
            configurable: true
        });
    }
}

const SUPPLIER_ACCESS_VIEW_ONLY = 'ViewOnly';
const SUPPLIER_ACCESS_PRIVILEGED = 'Privileged';
const REPLENISHMENT_DASHBOARD_ID = '1100';
const SUPPLIER_OVERVIEW_DASHBOARD_ID = '1200';
const STORE_TURNOVER_DASHBOARD_ID = '1201';

function getDashboardIdFromAccess(access, fallback) {
    if (access != null) {
        return String(access);
    }
    return fallback;
}

function getInitialDashboardId() {
    const supplierAccess = localStorageService.getSupplierAccess();
    if (supplierAccess?.defaultDashboardId != null) {
        return String(supplierAccess.defaultDashboardId);
    }
    return '0';
}

function getCurrentYearMonth() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${now.getFullYear()}-${month}`;
}

function getReplenishmentDashboardId() {
    const supplierAccess = localStorageService.getSupplierAccess();
    return getDashboardIdFromAccess(
        supplierAccess?.replenishmentDashboardId,
        REPLENISHMENT_DASHBOARD_ID
    );
}

function dashboardUsesExclusivePeriodFilters(dashboardId) {
    return String(dashboardId) === getReplenishmentDashboardId();
}

function getSupplierAccessMessage() {
    const supplierAccess = localStorageService.getSupplierAccess();
    if (supplierAccess?.mode === SUPPLIER_ACCESS_VIEW_ONLY && (!supplierAccess.supplierId || supplierAccess.supplierId === 0)) {
        return 'Ingen leverandør er koblet til brukeren din. Kontakt butikkadministrator.';
    }
    return null;
}

function shouldBlockDashboardLoad() {
    return !!getSupplierAccessMessage();
}

class Dashboard extends React.Component {
    constructor(props) {
        super(props);
        this.dashboardRef = React.createRef();
        this.state = {
            permission: 'Viewer',
            dashboardId: getInitialDashboardId(),
            supplierAccessMessage: getSupplierAccessMessage()
        };
        this._periodFilterState = { Items: {} };
        this._syncingPeriodFilters = false;
        this._periodFilterInitInProgress = false;
        this._periodFilterSyncTimer = null;
        this._onPayoutLayoutResize = () => {
            if (!isPayoutDashboard(this.state.dashboardId)) {
                return;
            }
            this.updatePayoutLayoutShell(this.state.dashboardId);
        };
    }

    updatePayoutLayoutShell = (dashboardId) => {
        const scrollEl = document.getElementById('scrollviewdash');
        const hostEl = this.dashboardRef.current;
        if (!scrollEl || !hostEl) {
            return;
        }

        const payout = isPayoutDashboard(dashboardId);
        const height = getDashboardLayoutHeight(dashboardId);
        scrollEl.classList.toggle('payout-dashboard', payout);
        hostEl.style.height = `${height}px`;
        scrollEl.style.height = payout ? `${height}px` : '';

        if (payout && this.dashboardControl?.dashboard?.()) {
            applyDashboardLayoutHeight(this.dashboardControl.dashboard(), dashboardId);
        }
    };
    dashboardChanged = async (e) => {
        let workingMode;
        if (e.name === "dashboardId") {
            if (this.dashboardControl
                && this._lastDashboardId !== undefined
                && this._lastDashboardId !== e.value) {
                // Avoid stale item state from another dashboard (e.g. cardDashboardItem1 on Supplier).
                this.dashboardControl.setDashboardState('{}');
                this._periodFilterState = { Items: {} };
            }
            this._lastDashboardId = e.value;
            if (e.value === "0" && this.dashboardControl) {
                this.dashboardControl.switchToViewer();
                this.applyStandardDashboardInitialState();
            }
            const supplierAccess = localStorageService.getSupplierAccess();
            const replenishmentId = getDashboardIdFromAccess(
                supplierAccess?.replenishmentDashboardId,
                REPLENISHMENT_DASHBOARD_ID
            );
            const overviewId = getDashboardIdFromAccess(
                supplierAccess?.supplierOverviewDashboardId,
                SUPPLIER_OVERVIEW_DASHBOARD_ID
            );
            const payoutId = SUPPLIER_PAYOUT_DASHBOARD_ID;
            if (e.value === replenishmentId && this.dashboardControl) {
                this.applySupplierReplenishmentInitialState();
            }
            if ((e.value === replenishmentId || e.value === overviewId || e.value === payoutId) && this.dashboardControl) {
                this.applyYearMonthInitialState();
            }
            if (e.value > "0") {
                workingMode = await webDashboardService.GetWorkingMode(e.value);
                if (workingMode === 'Viewer') {
                    this.dashboardControl.switchToViewer();
                }
                if (workingMode === 'Designer') {
                    this.dashboardControl.switchToDesigner();
                }
            }
        }
        this.setState({
            permission: workingMode ? workingMode : 'Viewer',
            dashboardId: e.value
        });
        if (e.name === 'dashboardId') {
            window.setTimeout(() => this.updatePayoutLayoutShell(e.value), 0);
        }
    };

    applyStandardDashboardInitialState = () => {
        if (!this.dashboardControl) {
            return;
        }
        const currentDate = new Date();
        this.dashboardControl.setDashboardState(JSON.stringify({
            Items: {
                comboBoxDashboardItem1: {
                    MasterFilterValues: [[currentDate.getMonth() + 1]]
                }
            }
        }));
    };

    applyYearMonthInitialState = () => {
        if (!this.dashboardControl) {
            return;
        }
        const yearMonth = getCurrentYearMonth();
        const initialState = buildYearMonthOnlyState(yearMonth);
        const stateJson = JSON.stringify(initialState);
        this._periodFilterState = initialState;
        this._periodFilterInitInProgress = true;
        const applyState = () => {
            if (this.dashboardControl) {
                this.dashboardControl.setDashboardState(stateJson);
            }
        };
        applyState();
        window.setTimeout(applyState, 300);
        window.setTimeout(() => {
            this._periodFilterInitInProgress = false;
            this.syncPeriodFilters();
        }, 800);
    };

    applyReconciledPeriodState = (reconciledState) => {
        if (!reconciledState || !this.dashboardControl) {
            return;
        }
        const stateJson = JSON.stringify(reconciledState);
        this._syncingPeriodFilters = true;
        this._periodFilterState = reconciledState;
        this.dashboardControl.setDashboardState(stateJson);
        window.setTimeout(() => {
            this._syncingPeriodFilters = false;
        }, 0);
    };

    syncPeriodFilters = () => {
        if (!this.dashboardControl || !dashboardUsesExclusivePeriodFilters(this.state.dashboardId)) {
            return;
        }

        if (isPeriodPickerDialogOpen() || this._syncingPeriodFilters) {
            return;
        }

        const currentState = parseDashboardState(this.dashboardControl.getDashboardState());
        const reconciledState = reconcilePeriodFilters(this._periodFilterState, currentState);

        if (!reconciledState || JSON.stringify(reconciledState) === JSON.stringify(currentState)) {
            this._periodFilterState = currentState;
            return;
        }

        this.applyReconciledPeriodState(reconciledState);
    };

    applySupplierReplenishmentInitialState = () => {
        if (!this.dashboardControl) {
            return;
        }
        const supplierAccess = localStorageService.getSupplierAccess();
        if (!supplierAccess?.supplierName || !supplierAccess.supplierId) {
            return;
        }
        // View-only: combo is removed server-side; filter is enforced in API.
        if (supplierAccess.mode === SUPPLIER_ACCESS_VIEW_ONLY) {
            return;
        }
        // Privileged: pre-select linked supplier; combo still has ShowAllValue ("Alle") to view all suppliers.
        const stateJson = JSON.stringify({
            Items: {
                comboBoxSupplier: {
                    MasterFilterValues: [[supplierAccess.supplierName]]
                }
            }
        });
        const applyState = () => {
            if (this.dashboardControl) {
                this.dashboardControl.setDashboardState(stateJson);
            }
        };
        applyState();
        window.setTimeout(applyState, 300);
    };

    onDashboardInitialized = (e) => {
        if (e.dashboardId === '0') {
            this.applyStandardDashboardInitialState();
            return;
        }
        const supplierAccess = localStorageService.getSupplierAccess();
        const replenishmentId = getDashboardIdFromAccess(
            supplierAccess?.replenishmentDashboardId,
            REPLENISHMENT_DASHBOARD_ID
        );
        const overviewId = getDashboardIdFromAccess(
            supplierAccess?.supplierOverviewDashboardId,
            SUPPLIER_OVERVIEW_DASHBOARD_ID
        );
        if (e.dashboardId === replenishmentId) {
            this.applySupplierReplenishmentInitialState();
            this.applyYearMonthInitialState();
        }
        if (e.dashboardId === overviewId) {
            this.applyYearMonthInitialState();
        }
        if (e.dashboardId === SUPPLIER_PAYOUT_DASHBOARD_ID) {
            this.applyYearMonthInitialState();
            this.updatePayoutLayoutShell(e.dashboardId);
        }
    };

    onBeforeDashboardRender = () => {
    };

    onDashboardInitializing = (e) => {
        applyDashboardLayoutHeight(e.dashboard, e.dashboardId);
    };

    onDashboardStateChanged = (e) => {
        if (this._syncingPeriodFilters || !dashboardUsesExclusivePeriodFilters(e.dashboardId)) {
            return;
        }

        const nextState = parseDashboardState(e.stateString);
        if (this._periodFilterInitInProgress) {
            this._periodFilterState = nextState;
            return;
        }
        const reconciledState = reconcilePeriodFilters(this._periodFilterState, nextState);

        if (reconciledState) {
            this.applyReconciledPeriodState(reconciledState);
            return;
        }

        this._periodFilterState = nextState;
    };

    loadDashboards = () => {
        applyDashboardLocalization();
        installDashboardAjaxCompatibility();

        const userRole = localStorageService.getUserRoleType();
        const permission = this.verifyWorkingMode(userRole?.split(',')?.map((r) => r.trim()));
        this.editingAllowed = permission === "Designer";
        this.setState({ permission });

        this.dashboardControl = new DashboardControl(this.dashboardRef.current, {
            // Specifies the URL where the Web Dashboard's server side is hosted.
            endpoint: apiconst.ENDPOINT + apiconst.DASHBOARD,
            workingMode: this.state.permission,
            dashboardId: this.state.dashboardId,
            // DevExpress default true suppresses the "Show all" option in filter combos; false is required for supplier "Alle".
            useNeutralFilterMode: false,
            // SQL String editor in the Data Source Wizard: requires API AllowExecutingCustomSql = true (RetailLinkDashboardAPI / Portal).
            extensions: {
                'data-source-wizard': {
                    enableCustomSql: true
                },
                // DevExpress extension key is dashboardExport (not "export"); wrong key ignores pdfExportOptions.
                dashboardExport: {
                    allowExportDashboard: true,
                    pdfExportOptions: basePdfExportOptions,
                    onExportDialogShowing: function () {
                        const exportExtension = this.findExtension('dashboard-export');
                        exportExtension?.setPdfExportOptions?.(buildPdfExportOptions(this));
                    }
                },
                'viewer-api': {
                    onItemWidgetOptionsPrepared: (args) => {
                        onYearMonthComboWidgetOptionsPrepared(args);
                        onCommissionPercentGridWidgetOptionsPrepared(args);
                    }
                }
            },
            // workingMode: "ViewerOnly",
            MobileLayoutMode: 'Always',
            onBeforeRender: this.onBeforeDashboardRender,
            onDashboardInitializing: this.onDashboardInitializing,
            onOptionChanged: this.dashboardChanged,
            onDashboardInitialized: this.onDashboardInitialized,
            onDashboardStateChanged: this.onDashboardStateChanged,
            ajaxRemoteService: {
                headers: {
                    'authorization': "bearer " + localStorageService.getAuthToken()
                },
                complete: (jqXHR) => {
                    if (!jqXHR?.responseJSON || jqXHR.responseJSON.ConditionalFormattingModel !== null) {
                        return;
                    }
                    patchDashboardItemResponse(jqXHR.responseJSON);
                }
            }
        });
        const exportExtension = this.dashboardControl.findExtension('dashboard-export');
        if (exportExtension?.setPdfExportOptions) {
            exportExtension.setPdfExportOptions(buildPdfExportOptions(this.dashboardControl));
        }
        return this.dashboardControl;
    };

    verifyWorkingMode = (userRoles) => {
        if (!userRoles || !userRoles.length) {
            return "Viewer";
        }
        const editRights = [
            UserRoleType.EDITSTORE,
            UserRoleType.EDITCHAIN,
            UserRoleType.EDITCLUSTER
        ];
        const hasEdit = userRoles.some((r) => editRights.includes(r));
        if (hasEdit) {
            return "Designer";
        }
        const viewRights = [
            UserRoleType.VIEWSTORE,
            UserRoleType.VIEWCHAIN,
            UserRoleType.VIEWCLUSTER
        ];
        const hasView = userRoles.some((r) => viewRights.includes(r));
        return hasView ? "Viewer" : "Viewer";
    }

    checkAuthentication = async () => {
        let isAuthented = await authenticationService.CheckAuthentication();
        if (!isAuthented) {
            localStorageService.clearStorage();
            window.location.reload(true);
        }
        else if (shouldBlockDashboardLoad()) {
            this.setState({ supplierAccessMessage: getSupplierAccessMessage() });
        }
        else {
            if (this.dashboardControl === undefined) {
                const dashboardControl = this.loadDashboards();
                // The dashboardControl variable is the obtained DashboardControl instance.
                // var panelExtension = new DashboardPanelExtension(dashboardControl, { dashboardThumbnail: '/DashboardThumbnail/{0}.png' });
                var panelExtension = new DashboardPanelExtension(dashboardControl);
                dashboardControl.registerExtension(panelExtension);
                // Allow toolbar switch to Designer when user has Edit.* rights (matches API GetWorkingMode intent).
                panelExtension.allowSwitchToDesigner(this.editingAllowed);
                dashboardControl.render();
            }
        }
    }

    componentDidMount() {
        var a2hsBannerDiv = document.querySelector('.block__install');
        if (a2hsBannerDiv)
            a2hsBannerDiv.style.display = 'none';
        window.addEventListener('resize', this._onPayoutLayoutResize);
        this.checkAuthentication();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onPayoutLayoutResize);
        if (this.dashboardControl)
            this.dashboardControl.dispose();
    }
    onEndUpdate(args) {
        console.log(args);
    }

    render() {
        const { supplierAccessMessage } = this.state;
        const blockDashboard = !!supplierAccessMessage;
        return (
            <div>
                {supplierAccessMessage && (
                    <div
                        className="supplier-access-message"
                        style={{
                            padding: '12px 16px',
                            margin: '8px',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '4px',
                            color: '#664d03'
                        }}
                        role="alert"
                    >
                        {supplierAccessMessage}
                    </div>
                )}
                {!blockDashboard && (
                    <div
                        id="scrollviewdash"
                        className={`dashboard-page${isPayoutDashboard(this.state.dashboardId) ? ' payout-dashboard' : ''}`}
                    >
                        <div
                            ref={this.dashboardRef}
                            className="dashboard-host"
                            style={{
                                width: '100%',
                                height: `${getDashboardLayoutHeight(this.state.dashboardId)}px`
                            }}
                        ></div>
                    </div>
                )}
            </div>
        );
    }
}

export default Dashboard;